import React, { useEffect, useState } from "react";
import { Wind, Thermometer, Drop, CloudRain, ArrowUp } from "@phosphor-icons/react";
import { fetchWeather } from "../lib/api";

const DIRS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
  "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];

const dirDeg = (d) => {
  const i = DIRS.indexOf((d || "").toUpperCase());
  return i >= 0 ? i * 22.5 : null;
};

const hourLabel = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric" });
  } catch {
    return "";
  }
};

export const WeatherPanel = ({ lat, lon }) => {
  const [wx, setWx] = useState(null);

  useEffect(() => {
    if (lat == null || lon == null) return;
    fetchWeather(lat, lon).then(setWx).catch(() => setWx({ available: false }));
  }, [lat, lon]);

  if (!wx?.available || !wx.current) return null;
  const c = wx.current;
  const fromDeg = dirDeg(c.wind_dir);
  const highWind = (c.wind_mph || 0) >= 15;

  return (
    <div className="border border-tac-border bg-tac-surface/40 p-4" data-testid="weather-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wind size={14} weight="duotone" className="text-tac-info" />
          <span className="text-[15px] uppercase tracking-[0.15em] font-bold text-tac-faint">
            Fire Weather at This Incident
          </span>
        </div>
        {wx.near && <span className="text-[12px] font-mono-tac text-tac-faint">near {wx.near}</span>}
      </div>

      <div className="flex items-center gap-3 mb-3" data-testid="weather-current">
        <div
          className={`w-11 h-11 shrink-0 rounded-full border flex items-center justify-center ${
            highWind ? "border-tac-danger/60 bg-tac-danger/10" : "border-tac-info/50 bg-tac-info/10"
          }`}
          title={c.wind_dir ? `Wind from ${c.wind_dir}, blowing toward ${DIRS[(DIRS.indexOf(c.wind_dir) + 8) % 16]}` : "Wind"}
        >
          {fromDeg != null ? (
            <ArrowUp
              size={22}
              weight="bold"
              className={highWind ? "text-tac-danger" : "text-tac-info"}
              style={{ transform: `rotate(${fromDeg + 180}deg)` }}
            />
          ) : (
            <Wind size={20} className="text-tac-info" />
          )}
        </div>
        <div>
          <div className={`text-lg font-black leading-tight ${highWind ? "text-tac-danger" : "text-tac-text"}`}>
            Wind {c.wind_mph != null ? `${c.wind_mph} mph` : "—"} {c.wind_dir ? `from ${c.wind_dir}` : ""}
          </div>
          <div className="text-[13px] font-mono-tac text-tac-dim">
            {c.wind_dir ? `blowing toward ${DIRS[(DIRS.indexOf(c.wind_dir) + 8) % 16]} · ` : ""}{c.short}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3 text-center">
        <div className="border border-tac-border/60 py-1.5">
          <Thermometer size={11} className="inline text-tac-primary mr-1" />
          <span className="text-sm font-bold text-tac-text tabular">{c.temp_f != null ? `${c.temp_f}°F` : "—"}</span>
        </div>
        <div className="border border-tac-border/60 py-1.5">
          <Drop size={11} className="inline text-tac-info mr-1" />
          <span className="text-sm font-bold text-tac-text tabular">{c.humidity != null ? `${c.humidity}% RH` : "—"}</span>
        </div>
        <div className="border border-tac-border/60 py-1.5">
          <CloudRain size={11} className="inline text-tac-info mr-1" />
          <span className="text-sm font-bold text-tac-text tabular">{c.precip_pct != null ? `${c.precip_pct}%` : "—"}</span>
        </div>
      </div>

      <div className="grid grid-cols-6 gap-1" data-testid="weather-hours">
        {wx.periods.slice(1, 7).map((p) => (
          <div key={p.time} className="border border-tac-border/50 py-1 text-center">
            <div className="text-[10px] font-mono-tac text-tac-faint">{hourLabel(p.time)}</div>
            <div className="text-[11px] font-bold text-tac-text tabular">{p.wind_mph ?? "—"}</div>
            <div className="text-[10px] font-mono-tac text-tac-dim">{p.wind_dir || ""}</div>
          </div>
        ))}
      </div>
      <div className="text-[11px] font-mono-tac text-tac-faint mt-2">
        Hourly wind mph + direction (from) — NWS forecast. Wind drives fire spread; gusty shifts can turn a fire fast.
      </div>
    </div>
  );
};

export default WeatherPanel;
